import { months } from './months';

export const format = {
  // Formatação numérica pt-BR: separador de milhar (ponto) + decimal (vírgula).
  // `toLocaleString('pt-BR', …)` cobre os dois de uma vez, com as casas fixadas
  // por `precision` (mín = máx → sempre `precision` casas, como o antigo toFixed).
  number(value: number, precision = 2) {
    if (!value) return '0';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  },
  // Formatação SEM separador de milhar (vírgula decimal apenas). Uso restrito:
  // semear inputs editáveis cujo valor é reparseado no submit com
  // `.replace(',', '.')` — o separador de milhar (ponto) quebraria esse parse.
  // Não usar para exibição; para exibir, use `number`/`money`/`energy`.
  plain(value: number, precision = 2) {
    if (!value) return '0';
    return value.toFixed(precision).replace('.', ',');
  },
  // Valores de energia (kWh): sempre inteiros (sem casas decimais) + milhar.
  energy(value: number) {
    if (!value) return '0';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  },
  // Tarifas e preços por kWh (R$/kWh): sempre 5 casas + milhar — o PCAT da
  // ANEEL publica em R$/MWh com 2 casas, ou seja, R$/kWh com 5 (decisão do
  // Bruno, 30/out/2026). Antes cada tela usava 2, 3 ou 4 casas.
  tariff(value: number) {
    return this.number(value, 5);
  },
  // Demanda/potência em kW: sempre 2 casas + milhar (5,5 kW ≠ 6 kW). Antes as
  // telas variavam entre 0 e 1 casa.
  demand(value: number) {
    return this.number(value, 2);
  },
  money(value: number) {
    if (!value) {
      if (value === 0) {
        return '0,00';
      }
      return;
    }
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },
  percentage(value: number) {
    if (!value) {
      return '0,00';
    }
    const perc = value * 100;
    return perc.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },
  percentageNoDecimal(value: number) {
    if (!value) {
      return '0';
    }
    const perc = value * 100;
    const fixed = perc.toFixed(0);
    return fixed;
  },
  date(value: Date) {
    if (!value) return;
    const date = new Date(value);
    // Datas de fatura são valores de CALENDÁRIO (dd/mm/aaaa), não instantes.
    // `due_date` é coluna `date` no banco → volta como 00:00Z; sem timeZone o
    // toLocaleString usa o fuso local e, em horário de Brasília (UTC-3), mostra
    // o DIA ANTERIOR. Ancorar em UTC exibe a data-calendário correta em qualquer
    // fuso, e independe de a fatura ter sido reextraída após o fix do extrator.
    return date.toLocaleString('pt-BR', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  },
  refMonth(value: string, digestYear = false) {
    if (!value) return '';
    if (digestYear) {
      const month = this.firstLetterUpperCase(
        months[Number(value.slice(-2)) - 1].toLowerCase()
      );
      return month.concat(`/${value.slice(2, 4)}`);
    }
    return months[Number(value.slice(-2)) - 1].concat(`/${value.slice(0, 4)}`);
  },
  firstLetterUpperCase(value: string) {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  },
};
